import { useLiveData } from "./useLiveData";
import { useEffect, useRef } from "react";
import { liveScoreToday, liveOdds } from "@/config";
import { buildMatchUpdateData } from "@/utils/liveScore";
import {
  getEventSource,
  subscribeToLiveScores,
} from "@/services/EventSourceService";

export function useLiveUpdates(matchIds = []) {
  const { updateAllMatchData, updateMatchOdds } = useLiveData();
  const lastDataRef = useRef(new Map());
  const matchIdsRef = useRef(matchIds);
  matchIdsRef.current = matchIds;

  useEffect(() => {
    getEventSource("liveScoreToday", liveScoreToday);
    const unsubscribeLiveScoreToday = subscribeToLiveScores(
      "liveScoreToday",
      (batchData) => {
        if (!batchData || !batchData.liveScoreToday) return;

        const matchIdsSet = new Set(matchIdsRef.current);
        for (const matchId in batchData.liveScoreToday) {
          const matchIdNum = parseInt(matchId, 10);
          if (matchIdsSet.has(matchIdNum)) {
            const updateData = buildMatchUpdateData(batchData, matchId);
            if (updateData) {
              const dataKey = `score_${matchId}`;
              const newDataStr = JSON.stringify(updateData);
              if (lastDataRef.current.get(dataKey) !== newDataStr) {
                lastDataRef.current.set(dataKey, newDataStr);
                updateAllMatchData(matchId, updateData);
              }
            }
          }
        }
      },
    );

    getEventSource("liveOdds", liveOdds);
    const unsubscribeLiveOdds = subscribeToLiveScores(
      "liveOdds",
      (batchData) => {
        if (
          !batchData ||
          typeof batchData !== "object" ||
          Array.isArray(batchData)
        )
          return;

        const matchIdsSet = new Set(matchIdsRef.current);
        for (const matchId in batchData) {
          const matchIdNum = parseInt(matchId, 10);
          if (matchIdsSet.has(matchIdNum)) {
            const oddsData = batchData[matchId];
            if (oddsData) {
              const dataKey = `odds_${matchId}`;
              const newOddsStr = JSON.stringify(oddsData);
              if (lastDataRef.current.get(dataKey) !== newOddsStr) {
                lastDataRef.current.set(dataKey, newOddsStr);
                updateMatchOdds(matchIdNum, oddsData);
              }
            }
          }
        }
      },
    );

    return () => {
      unsubscribeLiveOdds();
      unsubscribeLiveScoreToday();
    };
  }, [updateAllMatchData, updateMatchOdds]);

  return {};
}
